import React, { useState, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableSortLabel,
  Checkbox,
  ListItemText,
  ListItem,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { useReviews } from '../hooks/useReviews';
import { useTasks } from '../hooks/useTasks';
import type { Review } from '@segmently/schema-models/entities';
import type { ReviewFormData, SortDirection } from '../types';

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatDateForInput(dateString: unknown): string {
  if (!dateString) return '';
  try {
    let date: Date;
    if (typeof dateString === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      date = new Date(dateString);
    } else if (dateString instanceof Date) {
      date = dateString;
    } else {
      const ts = dateString as { toDate?: () => Date };
      date = ts.toDate ? ts.toDate() : new Date(dateString as string);
    }
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return '';
    }
    return date.toISOString().split('T')[0];
  } catch (err) {
    console.error('Error formatting date for input:', dateString, err);
    return '';
  }
}

function formatDate(dateString: unknown): string {
  if (!dateString) return '';
  try {
    let date: Date;
    if (typeof dateString === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        date = new Date(dateString + 'T00:00:00');
      } else {
        date = new Date(dateString);
      }
    } else if (dateString instanceof Date) {
      date = dateString;
    } else {
      const ts = dateString as { toDate?: () => Date };
      date = ts.toDate ? ts.toDate() : new Date(dateString as string);
    }
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return String(dateString);
    }
    return date.toLocaleDateString();
  } catch (err) {
    console.error('Error formatting date:', dateString, err);
    return String(dateString);
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortField = 'nickname' | 'rating' | 'reviewtext' | 'reviewdate';

interface ReviewWithTaskNames extends Review {
  taskNames: { id: string; name: string }[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ReviewsTable({
  projectId,
}: {
  projectId: string;
}) {
  const guideService = useMemo(() => new GuideService(), []);

  // Data hooks
  const { reviews, loading, error, setError, createReview, updateReview, deleteReview } =
    useReviews(projectId);
  const tasks = useTasks(projectId);

  // CRUD state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReview, setSelectedReview] = useState<ReviewWithTaskNames | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<ReviewFormData>({
    nickname: '',
    rating: 0,
    reviewtext: '',
    reviewdate: new Date().toISOString().split('T')[0],
    selectedTaskIds: [],
  });

  // Task selection dialog state
  const [openTaskSelectionDialog, setOpenTaskSelectionDialog] = useState(false);
  const [tempSelectedTaskIds, setTempSelectedTaskIds] = useState<string[]>([]);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('reviewdate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === reviews.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reviews.map(r => r.id!).filter(Boolean)));
    }
  }, [selectedIds.size, reviews]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => deleteReview(id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error deleting selected reviews:', err);
      setError('Failed to delete selected reviews.');
    }
  }, [selectedIds, deleteReview, setError]);

  // ---------------------------------------------------------------------------
  // Derive reviews with task names
  // ---------------------------------------------------------------------------

  const reviewsWithTaskNames: ReviewWithTaskNames[] = useMemo(() => {
    return reviews.map((review) => {
      const taskIds =
        (review as unknown as Record<string, unknown>).taskIds as string[] | undefined ||
        review.selectedTaskIds ||
        [];
      const taskNames = taskIds
        .map((tid: string) => {
          const task = tasks.find((t) => t.id === tid);
          return task ? { id: tid, name: task.name } : null;
        })
        .filter(Boolean) as { id: string; name: string }[];
      return { ...review, taskNames };
    });
  }, [reviews, tasks]);

  // ---------------------------------------------------------------------------
  // Sorting
  // ---------------------------------------------------------------------------

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedReviews = useMemo(() => {
    const sorted = [...reviewsWithTaskNames];
    sorted.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (sortField === 'rating') {
        aValue = Number(a[sortField]) || 0;
        bValue = Number(b[sortField]) || 0;
      } else if (sortField === 'reviewdate') {
        aValue = new Date(a[sortField] || 0).getTime();
        bValue = new Date(b[sortField] || 0).getTime();
      } else {
        aValue = ((a[sortField] as string) || '').toLowerCase();
        bValue = ((b[sortField] as string) || '').toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [reviewsWithTaskNames, sortField, sortDirection]);

  // ---------------------------------------------------------------------------
  // CRUD Operations
  // ---------------------------------------------------------------------------

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, review: ReviewWithTaskNames) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedReview(review);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReview(null);
  };

  const handleRowClick = (review: ReviewWithTaskNames) => {
    if (!review || !review.id) {
      setError('Invalid review data.');
      return;
    }
    setSelectedReview(review);
    setDialogMode('edit');
    setFormData({
      nickname: review.nickname || '',
      rating: review.rating || 0,
      reviewtext: review.reviewtext || '',
      reviewdate: formatDateForInput(review.reviewdate),
      selectedTaskIds:
        (review as unknown as unknown as Record<string, unknown>).taskIds as string[] || review.selectedTaskIds || [],
    });
    setOpenDialog(true);
  };

  const handleCreateReview = () => {
    setDialogMode('create');
    setFormData({
      nickname: '',
      rating: 0,
      reviewtext: '',
      reviewdate: new Date().toISOString().split('T')[0],
      selectedTaskIds: [],
    });
    setOpenDialog(true);
  };

  const handleEditReview = () => {
    if (!selectedReview) {
      setError('No review selected for editing.');
      return;
    }
    setDialogMode('edit');
    setFormData({
      nickname: selectedReview.nickname || '',
      rating: selectedReview.rating || 0,
      reviewtext: selectedReview.reviewtext || '',
      reviewdate: formatDateForInput(selectedReview.reviewdate),
      selectedTaskIds:
        (selectedReview as unknown as unknown as Record<string, unknown>).taskIds as string[] ||
        selectedReview.selectedTaskIds ||
        [],
    });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDeleteReview = async () => {
    if (!selectedReview) return;
    try {
      await deleteReview(selectedReview.id!);
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting review:', err);
      setError('Failed to delete review.');
    }
  };

  // ---------------------------------------------------------------------------
  // Task selection dialog handlers
  // ---------------------------------------------------------------------------

  const handleOpenTaskSelection = () => {
    setTempSelectedTaskIds([...formData.selectedTaskIds]);
    setOpenTaskSelectionDialog(true);
  };

  const handleCloseTaskSelection = () => {
    setOpenTaskSelectionDialog(false);
  };

  const handleConfirmTaskSelection = () => {
    setFormData({ ...formData, selectedTaskIds: tempSelectedTaskIds });
    setOpenTaskSelectionDialog(false);
  };

  const handleTaskToggle = (taskId: string) => {
    setTempSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId],
    );
  };

  const handleRemoveTask = (taskId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedTaskIds: prev.selectedTaskIds.filter((id) => id !== taskId),
    }));
  };

  // ---------------------------------------------------------------------------
  // Save / Cancel
  // ---------------------------------------------------------------------------

  const handleSaveReview = async () => {
    try {
      const reviewData: Partial<Review> = {
        nickname: formData.nickname,
        rating: Number(formData.rating),
        reviewtext: formData.reviewtext,
        reviewdate: formData.reviewdate ? new Date(formData.reviewdate) : new Date(),
        selectedTaskIds: formData.selectedTaskIds,
      };

      if (dialogMode === 'create') {
        await createReview(reviewData);
      } else {
        if (!selectedReview || !selectedReview.id) {
          setError('Cannot update review: Review data is missing.');
          return;
        }
        await updateReview(selectedReview.id, reviewData);
      }

      setOpenDialog(false);
      setSelectedReview(null);
      setFormData({
        nickname: '',
        rating: 0,
        reviewtext: '',
        reviewdate: new Date().toISOString().split('T')[0],
        selectedTaskIds: [],
      });
    } catch (err) {
      console.error('Error saving review:', err);
      setError('Failed to save review.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setFormData({
      nickname: '',
      rating: 0,
      reviewtext: '',
      reviewdate: new Date().toISOString().split('T')[0],
      selectedTaskIds: [],
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            Reviews
          </Typography>
          {selectedIds.size > 0 && (
            <>
              <Chip label={selectedIds.size} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              <Tooltip title="Delete selected">
                <IconButton onClick={handleDeleteSelected} color="error" size="small" aria-label="Delete selected">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <WithGuideHelp
          guideKey="create-review-button"
          guideService={guideService}
          position="top-right"
        >
          <Button
            data-testid="reviews-create-button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateReview}
            size="small"
            disabled={!projectId}
          >
            Create Review
          </Button>
        </WithGuideHelp>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table data-testid="reviews-table" sx={{ minWidth: 650 }} aria-label="reviews table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={reviews.length > 0 && selectedIds.size === reviews.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < reviews.length}
                  onChange={handleSelectAll}
                  disabled={reviews.length === 0}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 160 }}>
                <TableSortLabel
                  active={sortField === 'nickname'}
                  direction={sortField === 'nickname' ? sortDirection : 'asc'}
                  onClick={() => handleSort('nickname')}
                >
                  Nickname
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 90 }} align="right">
                <TableSortLabel
                  active={sortField === 'rating'}
                  direction={sortField === 'rating' ? sortDirection : 'asc'}
                  onClick={() => handleSort('rating')}
                >
                  Rating
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortField === 'reviewtext'}
                  direction={sortField === 'reviewtext' ? sortDirection : 'asc'}
                  onClick={() => handleSort('reviewtext')}
                >
                  Review Text
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortField === 'reviewdate'}
                  direction={sortField === 'reviewdate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('reviewdate')}
                >
                  Review Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', maxWidth: 200 }}>Tasks</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedReviews.length > 0 ? (
              sortedReviews.map((review) => (
                <TableRow
                  key={review.id}
                  data-testid={`reviews-row-${review.id}`}
                  hover
                  onClick={() => handleRowClick(review)}
                  sx={{ cursor: 'pointer' }}
                  selected={selectedIds.has(review.id!)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(review.id!)}
                      onChange={() => handleToggleSelect(review.id!)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                    {review.nickname || ''}
                  </TableCell>
                  <TableCell sx={{ width: 90 }} align="right">
                    {review.rating !== undefined && review.rating !== null
                      ? Number(review.rating)
                      : ''}
                  </TableCell>
                  <TableCell
                    sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}
                  >
                    {review.reviewtext || ''}
                  </TableCell>
                  <TableCell>{formatDate(review.reviewdate)}</TableCell>
                  <TableCell
                    sx={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}
                  >
                    {review.taskNames && review.taskNames.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {review.taskNames.map((task, idx) => (
                          <Chip
                            key={idx}
                            label={task.name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, review)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No reviews found. Click &quot;Create Review&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        data-testid="reviews-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem onClick={handleEditReview}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteReview} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog
        data-testid="reviews-dialog"
        open={openDialog}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            padding: '0 0 8px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCancel}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {dialogMode === 'create' ? 'Create New Review' : 'Edit Review'}
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={{ padding: '0', my: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nickname"
              value={formData.nickname}
              onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
              fullWidth
              inputProps={{ 'data-testid': 'reviews-field-nickname' }}
            />
            <TextField
              label="Rating (0-10)"
              type="number"
              value={formData.rating}
              onChange={(e) => {
                const next = Math.max(0, Math.min(10, Number(e.target.value)));
                setFormData({ ...formData, rating: next });
              }}
              fullWidth
              inputProps={{ min: 0, max: 10, step: 1, 'data-testid': 'reviews-field-rating' }}
            />
            <TextField
              label="Review Text"
              value={formData.reviewtext}
              onChange={(e) => setFormData({ ...formData, reviewtext: e.target.value })}
              multiline
              rows={4}
              fullWidth
              inputProps={{ 'data-testid': 'reviews-field-reviewtext' }}
            />
            <TextField
              label="Review Date"
              type="date"
              value={formData.reviewdate}
              onChange={(e) => setFormData({ ...formData, reviewdate: e.target.value })}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ 'data-testid': 'reviews-field-reviewdate' }}
            />

            {/* Tasks Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tasks
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Button variant="contained" onClick={handleOpenTaskSelection} size="small">
                  Select Tasks
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.selectedTaskIds.map((taskId) => {
                  const task = tasks.find((t) => t.id === taskId);
                  return (
                    <Chip
                      key={taskId}
                      label={task ? task.name : taskId}
                      onDelete={() => handleRemoveTask(taskId)}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button
            data-testid="reviews-save-button"
            onClick={handleSaveReview}
            variant="contained"
          >
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Selection Dialog */}
      <Dialog
        data-testid="reviews-task-dialog"
        open={openTaskSelectionDialog}
        onClose={handleCloseTaskSelection}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            padding: '0 0 8px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCloseTaskSelection}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Select Tasks
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={{ padding: '0', my: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tasks.map((task) => (
              <ListItem key={task.id} dense>
                <Checkbox
                  edge="start"
                  checked={tempSelectedTaskIds.includes(task.id)}
                  onChange={() => handleTaskToggle(task.id)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={task.name} />
              </ListItem>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button onClick={handleConfirmTaskSelection} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
